import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCustomers } from '../../services/zohoApi';
import { checkEmailExists, registerClient } from '../../services/localDbApi'; // Service unifié avec cookies
import { useClientPortal } from '../../contexts/ClientPortalContext';
import logo from '../../images/logo.png';

const SmartClientLogin = ({ onClose }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [step, setStep] = useState('email'); // 'email', 'existing-password', 'new-password'
    const [customerInfo, setCustomerInfo] = useState(null);
    const navigate = useNavigate();
    const { login } = useClientPortal();

    const isModal = !!onClose; // Determine if used as modal or standalone page

    const handleSuccessfulLogin = async (loginCredentials) => {
        try {
            // Utiliser la nouvelle méthode de login avec cookies
            await login(loginCredentials);
            
            if (isModal) {
                // Modal mode: close modal
                onClose();
            } else {
                // Standalone page mode: navigate
                navigate('/client-portal');
            }
        } catch (error) {
            setError('Erreur lors de la connexion: ' + error.message);
        }
    };

    // Step 1: Check if email exists in Zoho customers
    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) {
            setError('Please enter your email address');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            // Get all customers from Zoho
            const customers = await getCustomers();
            const customer = customers.find(c => c.email === email.toLowerCase().trim());

            if (!customer) {
                setError('Email not found. Please contact your administrator to get access.');
                setIsLoading(false);
                return;
            }

            setCustomerInfo(customer);

            // Check if customer already has an account in JWT backend
            try {
                // Check if email exists in our database
                const emailExists = await checkEmailExists(customer.email);
                if (emailExists) {
                    setStep('existing-password');
                } else {
                    setStep('new-password');
                }
            } catch (err) {
                console.error('Error checking email existence:', err);
                // If error, assume new account
                setStep('new-password');
            }

        } catch (err) {
            setError('Error checking email: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Step 2: Login with existing password - JWT VERSION
    const handleExistingPasswordSubmit = async (e) => {
        e.preventDefault();
        if (!password.trim()) {
            setError('Please enter your password');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            // Use nouvelle méthode avec cookies
            await handleSuccessfulLogin({ 
                email: customerInfo.email, 
                password: password 
            });

        } catch (err) {
            setError('Incorrect password. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Step 3: Create new password (first time login) - JWT VERSION
    const handleNewPasswordSubmit = async (e) => {
        e.preventDefault();
        
        if (!password.trim()) {
            setError('Please enter a password');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters long');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            // Register new user with JWT backend
            await registerClient({ 
                email: customerInfo.email, 
                password: password,
                name: customerInfo.contact_name,
                contact_id: customerInfo.contact_id
            });

            // Now login with the new account using cookies
            await handleSuccessfulLogin({ 
                email: customerInfo.email, 
                password: password
            });

        } catch (err) {
            setError('Error creating account: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackToEmail = () => {
        setStep('email');
        setPassword('');
        setConfirmPassword('');
        setError('');
        setCustomerInfo(null);
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            position: isModal ? 'fixed' : 'static',
            top: isModal ? 0 : 'auto',
            left: isModal ? 0 : 'auto',
            right: isModal ? 0 : 'auto',
            bottom: isModal ? 0 : 'auto',
            zIndex: isModal ? 1000 : 'auto'
        }}>
            <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '40px',
                maxWidth: '400px',
                width: '100%',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                position: 'relative'
            }}>
                {/* Close button for modal mode */}
                {isModal && (
                    <button
                        onClick={onClose}
                        style={{
                            position: 'absolute',
                            top: '15px',
                            right: '15px',
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            color: '#999',
                            cursor: 'pointer',
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = '#f8f9fa';
                            e.target.style.color = '#333';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = 'none';
                            e.target.style.color = '#999';
                        }}
                    >
                        ×
                    </button>
                )}
                <div style={{
                    textAlign: 'center',
                    marginBottom: '30px'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '15px'
                    }}>
                        <img 
                            src={logo} 
                            alt="Logo" 
                            style={{
                                height: '40px',
                                width: 'auto',
                                marginRight: '12px'
                            }}
                        />
                        <h1 style={{
                            color: '#333',
                            fontSize: '28px',
                            fontWeight: '600',
                            margin: 0
                        }}>
                            Distributor Portal
                        </h1>
                    </div>
                    <p style={{
                        color: '#666',
                        fontSize: '16px',
                        margin: 0
                    }}>
                        {step === 'email' && 'Enter your email to access the portal'}
                        {step === 'existing-password' && `Welcome back, ${customerInfo?.contact_name}!`}
                        {step === 'new-password' && `Hello ${customerInfo?.contact_name}! Create your password`}
                    </p>
                </div>

                {error && (
                    <div style={{
                        background: '#ffebee',
                        color: '#c62828',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: '1px solid #ffcdd2',
                        fontSize: '14px'
                    }}>
                        {error}
                    </div>
                )}

                {/* Step 1: Email Entry */}
                {step === 'email' && (
                    <form onSubmit={handleEmailSubmit}>
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '8px',
                                color: '#333',
                                fontWeight: '500'
                            }}>
                                Email Address
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="your.email@company.com"
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    border: '2px solid #dee2e6',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    transition: 'border-color 0.3s ease',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                onBlur={(e) => e.target.style.borderColor = '#dee2e6'}
                                required
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            style={{
                                width: '100%',
                                padding: '12px',
                                background: isLoading ? '#6c757d' : '#667eea',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: '600',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                transition: 'background 0.3s ease'
                            }}
                        >
                            {isLoading ? 'Checking...' : 'Continue'}
                        </button>
                    </form>
                )}

                {/* Step 2: Existing Password */}
                {step === 'existing-password' && (
                    <form onSubmit={handleExistingPasswordSubmit}>
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '8px',
                                color: '#333',
                                fontWeight: '500'
                            }}>
                                Password
                            </label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter your password"
                                autoComplete="current-password"
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    border: '2px solid #dee2e6',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    transition: 'border-color 0.3s ease',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                onBlur={(e) => e.target.style.borderColor = '#dee2e6'}
                                required
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                type="button"
                                onClick={handleBackToEmail}
                                style={{
                                    flex: '1',
                                    padding: '12px',
                                    background: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                Back
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading}
                                style={{
                                    flex: '2',
                                    padding: '12px',
                                    background: isLoading ? '#6c757d' : '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: isLoading ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {isLoading ? 'Logging in...' : 'Login'}
                            </button>
                        </div>
                    </form>
                )}

                {/* Step 3: New Password Creation */}
                {step === 'new-password' && (
                    <form onSubmit={handleNewPasswordSubmit}>
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '8px',
                                color: '#333',
                                fontWeight: '500'
                            }}>
                                Create Password
                            </label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Minimum 6 characters"
                                autoComplete="new-password"
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    border: '2px solid #dee2e6',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    transition: 'border-color 0.3s ease',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                onBlur={(e) => e.target.style.borderColor = '#dee2e6'}
                                required
                            />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '8px',
                                color: '#333',
                                fontWeight: '500'
                            }}>
                                Confirm Password
                            </label>
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Re-enter your password"
                                autoComplete="new-password"
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    border: '2px solid #dee2e6',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    transition: 'border-color 0.3s ease',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                onBlur={(e) => e.target.style.borderColor = '#dee2e6'}
                                required
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                type="button"
                                onClick={handleBackToEmail}
                                style={{
                                    flex: '1',
                                    padding: '12px',
                                    background: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                Back
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading}
                                style={{
                                    flex: '2',
                                    padding: '12px',
                                    background: isLoading ? '#6c757d' : '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: isLoading ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {isLoading ? 'Creating...' : 'Create Account'}
                            </button>
                        </div>
                    </form>
                )}

                <div style={{
                    textAlign: 'center',
                    marginTop: '30px',
                    paddingTop: '20px',
                    borderTop: '1px solid #eee'
                }}>
                    <p style={{
                        color: '#666',
                        fontSize: '14px',
                        margin: 0
                    }}>
                        Need access? Contact your administrator
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SmartClientLogin;
